// src/views/Estados/FiltroTipoEstados.jsx
import React, { useEffect, useRef, useState } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";

const PAGE_SIZE = 50;

function useDebounced(v, delay = 300) {
  const [val, setVal] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setVal(v), delay);
    return () => clearTimeout(t);
  }, [v, delay]);
  return val;
}

const isAbort = (e) =>
  e?.name === "AbortError" || /aborted|abort/i.test(e?.message || "");

export default function FiltroTipoEstados({
  initialFilters = {
    q: "",
  },
  onSubmit = () => {},
  onClose = () => {},
}) {
  // Campo único: nombreTipoestado (front lo maneja como "q")
  const [nombreInput, setNombreInput] = useState(initialFilters.q || "");

  // Autocomplete
  const debNombre = useDebounced(nombreInput, 300);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);
  const boxRef = useRef(null);
  const abortRef = useRef(null);

  // Rehidratación si cambian los filtros externos
  useEffect(() => {
    setNombreInput(initialFilters.q || "");
  }, [initialFilters]);

  // Cerrar con ESC
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Cancelar request anterior
  const cancelReq = () => {
    try {
      abortRef.current?.abort();
    } catch {}
  };

  // Fetch paginado al backend
  const fetchAutocomplete = async (q, page, signal) => {
    const qTrim = (q ?? "").trim();
    const url = apiRoutes.estadoTipo.autocompleteUrl(
      qTrim,
      page ?? 0,
      PAGE_SIZE,
    );

    const data = await apiFetchJson(url, { signal });
    const rawItems = data?.items ?? [];

    const list = rawItems.map((x) => ({
      id: x.id,
      nombre: (x.nombre ?? "").toString().trim(),
    }));

    return {
      list,
      hasMore: Boolean(data?.hasMore),
      page: data?.page ?? page ?? 0,
    };
  };

  // Cargar primera página cuando abre o cambia el texto (debounced)
  useEffect(() => {
    if (!open) return;
    cancelReq();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const q = debNombre;
    setLoading(true);
    (async () => {
      try {
        const { list, hasMore, page } = await fetchAutocomplete(
          q,
          0,
          ctrl.signal,
        );
        setItems(list);
        setHasMore(hasMore);
        setPage(page);
      } catch (e) {
        if (!isAbort(e)) console.error("Autocomplete tipoEstado:", e);
      } finally {
        setLoading(false);
      }
    })();
    return cancelReq;
  }, [open, debNombre]);

  // Scroll infinito
  const onScroll = (e) => {
    const el = e.currentTarget;
    if (!hasMore || loading) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      const q = debNombre;
      const next = page + 1;
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      fetchAutocomplete(q, next, ctrl.signal)
        .then(({ list, hasMore, page }) => {
          setItems((prev) => [...prev, ...list]);
          setHasMore(hasMore);
          setPage(page);
        })
        .finally(() => setLoading(false));
    }
  };

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Submit
  const submit = (e) => {
    e.preventDefault();

    onSubmit({
      q: (nombreInput || "").trim(),
    });

    onClose();
  };

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div
        className="drawer-panel overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="drawer-header">
          <h2 className="text-title">Filtros</h2>
          <button onClick={onClose} className="btn-cancel" title="Cerrar">
            &times;
          </button>
        </div>

        {/* Formulario */}
        <form onSubmit={submit} className="drawer-body space-y-3">
          {/* Autocomplete Tipo de estado */}
          <div ref={boxRef} className="relative">
            <label className="form-label-fil">Tipo de estado:</label>
            <input
              type="text"
              className="input"
              placeholder="Buscar por nombre…"
              value={nombreInput}
              onChange={(e) => {
                const v = e.target.value;
                setNombreInput(v);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
            />
            {open && (
              <div
                className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl max-h-72 overflow-auto"
                onScroll={onScroll}
              >
                {items.map((it) => (
                  <button
                    key={it.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-gray-50"
                    onClick={() => {
                      setNombreInput(it.nombre || "");
                      setOpen(false);
                    }}
                  >
                    {it.nombre}
                  </button>
                ))}
                {loading && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    Cargando…
                  </div>
                )}
                {!loading && items.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    Sin resultados
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="text-right mt-4">
            <button type="submit" className="btn">
              Aplicar Filtros
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
